import dayjs from "dayjs";
import { useEffect, useEffectEvent, useRef, useState } from "react";

import { allowPurchaseExpiresAtStorage } from "@/utils/localStorage";

const ALLOW_MINUTES = 5;

const getRestoredSecondsLeft = () => {
  const stored = allowPurchaseExpiresAtStorage.get();
  if (!stored) return 0;

  const expiresAt = Number(stored);
  if (!expiresAt) return 0;

  const secondsLeft = dayjs(expiresAt).diff(dayjs(), "second");

  return Math.max(0, secondsLeft);
};

export function usePurchaseBlock() {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(() =>
    getRestoredSecondsLeft(),
  );

  const purchasesAllowed = secondsLeft > 0;

  const clearAllowPurchaseInterval = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  const disablePurchases = useEffectEvent(() => {
    allowPurchaseExpiresAtStorage.remove();
    clearAllowPurchaseInterval();
  });

  const setupAllowPurchaseInterval = () => {
    clearAllowPurchaseInterval();

    intervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
  };

  const startTimer = (remainingSeconds: number) => {
    setSecondsLeft(remainingSeconds);
    setupAllowPurchaseInterval();
  };

  const enablePurchases = () => {
    const totalSeconds = ALLOW_MINUTES * 60;
    const expiresAt = dayjs().add(ALLOW_MINUTES, "minute").valueOf();

    allowPurchaseExpiresAtStorage.set(String(expiresAt));
    startTimer(totalSeconds);
  };

  const handleClick = () => {
    if (purchasesAllowed) {
      setSecondsLeft(0);
    } else {
      enablePurchases();
    }
  };

  const restoreInterval = useEffectEvent(() => {
    if (getRestoredSecondsLeft() > 0) {
      setupAllowPurchaseInterval();
    } else {
      allowPurchaseExpiresAtStorage.remove();
      clearAllowPurchaseInterval();
    }

    return () => clearAllowPurchaseInterval();
  });

  useEffect(() => {
    restoreInterval();
  }, []);

  useEffect(() => {
    if (secondsLeft <= 0) {
      disablePurchases();
      clearInterval(intervalRef.current!);
    }
  }, [secondsLeft]);

  return {
    isPurchaseAllowed: purchasesAllowed,
    purchaseAllowedSecondsLeft: secondsLeft,
    togglePurchaseBlock: handleClick,
  };
}

export type UsePurchaseBlockReturn = ReturnType<typeof usePurchaseBlock>;
